import type { OrderDetail } from "./medusa"
import { extraLabel, withExtras } from "./order-items"

/**
 * What a packing slip says, from an order. Greek, because the slip goes into the parcel — and it
 * stays exactly this Greek whichever language HQ is shown in: a customer's parcel never changes with
 * the owner's setting. So nothing in this file goes through t(), and the date and money below are
 * fixed to "el-GR" on purpose instead of following lib/format.ts. The delivery labels are the
 * storefront's own (lib/delivery.ts there), so the customer reads the same words they chose at
 * checkout.
 */
export const SLIP_DELIVERY: Record<OrderDetail["delivery"], string> = {
  box_now: "BOX NOW locker",
  home: "Κατ’ οίκον παράδοση",
  meetup: "Meet-up στην Αθήνα",
  unknown: "Παράδοση",
}

/**
 * The slip's fixed words. They live here, away from the translated screens (routes/slips.tsx only
 * prints them), so the i18n guard has nothing to translate and a language switch cannot reach them.
 */
export const SLIP_TEXT = {
  instagram: "Instagram:",
  total: "Σύνολο",
  thanks: "Ευχαριστούμε για την παραγγελία! Για οτιδήποτε, στείλτε μας μήνυμα στο Instagram",
  handle: "@highclubgr",
} as const

const EUR = new Intl.NumberFormat("el-GR", { style: "currency", currency: "EUR" })
export const slipMoney = (amount: number) => EUR.format(amount)

/** Medusa's name for "no options" — never printed. */
const NO_VARIANT = /^(default( title| option| variant)?|title|one size)$/i

export type Slip = {
  ref: string
  date: string
  delivery: string
  name: string
  lines: string[]
  instagram: string | null
  items: Array<{ key: string; quantity: number; title: string; size: string | null; amount: number }>
  charges: Array<{ label: string; amount: number }>
  total: number
  payment: { text: string; due: number | null }
}

export function slipFor(order: OrderDetail): Slip {
  const place = [order.address.postcode, order.address.city].filter(Boolean).join(" ")
  const lines = [order.address.line, place, order.phone].filter((line): line is string => !!line && line.trim().length > 0)
  const refunded = order.payment.refunded ?? 0
  const due = order.payment.kind === "cash" && !order.payment.collected ? Math.max(0, order.total - refunded) : null
  return {
    ref: order.ref,
    date: order.created_at
      ? new Date(order.created_at).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Athens" })
      : "",
    delivery: SLIP_DELIVERY[order.delivery],
    name: order.customer,
    lines,
    instagram: order.instagram,
    // Each item, then the packaging chosen for it as a line of its own to tick («Συσκευασία: Premium …»).
    // A line taken out as out of stock (quantity 0) is not in the parcel.
    items: withExtras(order.items.filter((item) => item.quantity > 0)).flatMap((item) => [
      {
        key: item.id,
        quantity: item.quantity,
        title: item.title,
        size: item.variant && !NO_VARIANT.test(item.variant.trim()) ? item.variant.trim() : null,
        amount: item.unit_price * item.quantity,
      },
      ...item.extras.map((extra) => ({ key: extra.id, quantity: extra.quantity, title: extraLabel(extra), size: null, amount: extra.unit_price * extra.quantity })),
    ]),
    charges: [
      ...order.shipping.map((method) => ({ label: method.name ?? "Αποστολή", amount: method.amount })),
      ...(order.discount?.total > 0 ? [{ label: order.discount.codes.length ? `Έκπτωση (${order.discount.codes.join(", ")})` : "Έκπτωση", amount: -order.discount.total }] : []),
      ...(refunded > 0 ? [{ label: "Επιστροφή χρημάτων", amount: -refunded }] : []),
    ],
    total: order.total - refunded,
    payment:
      due != null
        ? { text: `Πληρωμή με μετρητά στην παράδοση: ${slipMoney(due)}`, due }
        : order.payment.kind === "cash"
          ? { text: "Πληρώθηκε με μετρητά", due: null }
          : order.payment.kind === "card"
            ? { text: "Πληρώθηκε με κάρτα", due: null }
            : { text: "", due: null },
  }
}

/** The slip page's address, for one order or a queue's worth. */
export const slipsUrl = (ids: string[]) => `/print/slips?ids=${ids.map(encodeURIComponent).join(",")}`
